import TiptapImage from '@tiptap/extension-image'
import {
    NodeViewWrapper,
    ReactNodeViewRenderer,
    type NodeViewProps,
} from '@tiptap/react'
import {
    AlignCenter,
    AlignLeft,
    AlignRight,
    Replace,
    Trash2,
} from 'lucide-react'
import { useState } from 'react'

import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from '@/components/ui/popover'
import { cn } from '@/lib/utils'
import { useTranslation } from 'react-i18next'

import { summonImagePicker } from './image-picker-storage'

type Align = 'left' | 'center' | 'right'

/**
 * Tagada email image node — extends the official Tiptap image with:
 *   - A React NodeView showing a floating action bar on selection
 *   - "Replace" opens the shared studio media library
 *   - Quick alignment + width + alt controls
 *   - Inline styles only (email-safe)
 */
export const ImageExtension = TiptapImage.extend({
    name: 'image',

    addAttributes() {
        const parent = this.parent?.() ?? {}
        return {
            ...parent,
            align: {
                default: 'center' as Align,
                parseHTML: (el: HTMLElement) =>
                    (el.getAttribute('data-align') as Align) || 'center',
                renderHTML: (attrs: Record<string, unknown>) => ({
                    'data-align': (attrs.align as Align) ?? 'center',
                }),
            },
            width: {
                default: null as string | null,
                parseHTML: (el: HTMLElement) =>
                    el.getAttribute('width') || el.style.width || null,
                renderHTML: (attrs: Record<string, unknown>) =>
                    attrs.width ? { width: attrs.width as string } : {},
            },
        }
    },

    addNodeView() {
        return ReactNodeViewRenderer(ImageNodeView)
    },
})

function ImageNodeView(props: NodeViewProps) {
    const { node, updateAttributes, deleteNode, selected, editor } = props
    const { t } = useTranslation('emails')
    const src = (node.attrs.src as string) || ''
    const alt = (node.attrs.alt as string) || ''
    const align = (node.attrs.align as Align) || 'center'
    const width = (node.attrs.width as string | null) || null
    const [popoverOpen, setPopoverOpen] = useState(false)

    const handleReplace = () => {
        summonImagePicker(editor ?? null, (url) => {
            updateAttributes({ src: url })
        })
    }

    const justifyClass =
        align === 'left'
            ? 'justify-start'
            : align === 'right'
              ? 'justify-end'
              : 'justify-center'

    return (
        <NodeViewWrapper
            className={cn(
                'email-builder-image-wrapper my-2 flex',
                justifyClass,
            )}
            data-align={align}
        >
            <div
                className={cn(
                    'group relative inline-block max-w-full rounded-md border-2 transition',
                    selected
                        ? 'border-primary/50 shadow-sm'
                        : 'border-transparent hover:border-muted',
                )}
            >
                {src ? (
                    <img
                        src={src}
                        alt={alt}
                        style={width ? { width } : undefined}
                        className="block h-auto max-w-full rounded-sm"
                    />
                ) : (
                    <button
                        type="button"
                        onClick={handleReplace}
                        className="flex h-32 w-64 flex-col items-center justify-center gap-1 rounded-sm bg-muted text-xs text-muted-foreground hover:bg-muted/80"
                    >
                        <Replace className="h-4 w-4" />
                        {t('builder.image.pickFromLibrary')}
                    </button>
                )}

                {(selected || popoverOpen) && src && (
                    <div className="absolute -top-9 left-1/2 z-10 flex -translate-x-1/2 items-center gap-0.5 rounded-md border bg-popover p-0.5 shadow-md">
                        <ToolbarButton
                            label={t('builder.image.replace')}
                            onClick={handleReplace}
                            icon={Replace}
                        />
                        <Divider />
                        <ToolbarButton
                            label={t('builder.image.alignLeft')}
                            active={align === 'left'}
                            onClick={() => updateAttributes({ align: 'left' })}
                            icon={AlignLeft}
                        />
                        <ToolbarButton
                            label={t('builder.image.alignCenter')}
                            active={align === 'center'}
                            onClick={() => updateAttributes({ align: 'center' })}
                            icon={AlignCenter}
                        />
                        <ToolbarButton
                            label={t('builder.image.alignRight')}
                            active={align === 'right'}
                            onClick={() => updateAttributes({ align: 'right' })}
                            icon={AlignRight}
                        />
                        <Divider />
                        <Popover open={popoverOpen} onOpenChange={setPopoverOpen}>
                            <PopoverTrigger asChild>
                                <button
                                    type="button"
                                    className="rounded px-2 py-1 text-[11px] font-medium hover:bg-muted"
                                    title={t('builder.image.sizeAndAlt')}
                                >
                                    {t('builder.image.edit')}
                                </button>
                            </PopoverTrigger>
                            <PopoverContent
                                side="top"
                                align="center"
                                className="w-64 space-y-3 p-3"
                            >
                                <div className="space-y-1">
                                    <Label className="text-[11px]">{t('builder.image.width')}</Label>
                                    <Input
                                        value={width || ''}
                                        placeholder={t('builder.image.widthPlaceholder')}
                                        onChange={(e) => {
                                            const v = e.target.value.trim()
                                            updateAttributes({
                                                width: v || null,
                                            })
                                        }}
                                        className="h-8 text-xs"
                                    />
                                </div>
                                <div className="space-y-1">
                                    <Label className="text-[11px]">
                                        {t('builder.image.altText')}
                                    </Label>
                                    <Input
                                        value={alt}
                                        placeholder={t('builder.image.altPlaceholder')}
                                        onChange={(e) =>
                                            updateAttributes({
                                                alt: e.target.value,
                                            })
                                        }
                                        className="h-8 text-xs"
                                    />
                                </div>
                            </PopoverContent>
                        </Popover>
                        <Divider />
                        <ToolbarButton
                            label={t('builder.image.delete')}
                            onClick={() => deleteNode()}
                            icon={Trash2}
                            className="text-destructive hover:bg-destructive/10"
                        />
                    </div>
                )}
            </div>
        </NodeViewWrapper>
    )
}

function ToolbarButton(props: {
    label: string
    icon: React.ComponentType<{ className?: string }>
    active?: boolean
    onClick: () => void
    className?: string
}) {
    const { label, icon: Icon, active, onClick, className } = props
    return (
        <button
            type="button"
            title={label}
            onClick={onClick}
            className={cn(
                'flex h-7 w-7 items-center justify-center rounded transition-colors hover:bg-muted',
                active && 'bg-muted text-foreground',
                className,
            )}
        >
            <Icon className="h-3.5 w-3.5" />
        </button>
    )
}

function Divider() {
    return <span className="mx-0.5 h-4 w-px bg-border" />
}
